import { observeWebVitals } from '../metrics/webVitals';
import { getCommonAttributes, getMeter, initTelemetrySdk, isTelemetrySdkInitialized } from './telemetrySdk';
import type { TelemetryConfig } from '../types';

/**
 * One-time, framework-agnostic telemetry initialization: creates the SDK and
 * registers Core Web Vitals collection. Calling this more than once is an
 * error and throws — telemetry is a process-wide singleton, so a second setup
 * almost always signals a wiring bug. React consumers should use
 * {@link TelemetryProvider}, which guards against repeated initialization (e.g.
 * remounts) rather than calling this directly on every mount.
 *
 * Mirrors the `setupApiSdk` convention in `@seeqdev/api`.
 */
export function setupTelemetry(config: TelemetryConfig): void {
  if (isTelemetrySdkInitialized()) {
    throw new Error('Telemetry has already been initialized. Multiple initializations are not supported.');
  }

  initTelemetrySdk(config);

  if (config.collectWebVitals !== false) {
    const meter = getMeter();
    // Guaranteed non-null immediately after a successful init; guarded for type
    // safety. Web vitals register at most once because setup throws on re-init.
    if (meter) {
      observeWebVitals(meter, getCommonAttributes);
    }
  }
}
